import { useCurrentFrame, useVideoConfig, staticFile } from 'remotion';

// Components
import { FallbackRemotionImg } from '@/components/common/FallbackRemotionImg';
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';
import { TypingText } from '@/components/common/animations/TypingText';

// Types
import { FeedbackCardProps } from '../types';

// Constants
import { LAYOUT_CONFIG, TIMING } from '../constants';

// Utils
import { combineClasses, processImageUrlForRemotion } from '@/utils';
import {
  getProfileAnimation,
  getNameTitleAnimation,
  getQuotationAnimation,
  getProjectCodeAnimation,
  getExitOpacity,
} from '../utils';

// Icons
import { QuotationMarksIcon } from '@shared/icons/QuotationMarksIcon';

// Themes
import { lalezarFontFamily } from '@/styles/fonts';

export const FeedbackCard = ({
  relatedAvatar,
  relatedName,
  role,
  feedbackHighlight,
  projectCode,
  variant,
  startFrame,
  transitionStartFrame,
}: FeedbackCardProps) => {
  const { typography, colors, rawColors } = employeeYearInReviewTheme;
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const config = LAYOUT_CONFIG[variant];

  const localFrame = frame - startFrame;

  // Normalize avatar URL (remove Google Image size params, validate format)
  const processedAvatarUrl = processImageUrlForRemotion(
    relatedAvatar,
    staticFile,
  );

  // Calculate exit animation progress
  const isExiting =
    transitionStartFrame !== undefined && frame >= transitionStartFrame;
  const exitFrame = isExiting ? frame - transitionStartFrame : 0;
  const exitProgress = isExiting
    ? Math.min(exitFrame / TIMING.TRANSITION_DURATION, 1)
    : 0;

  // Profile image animation
  const profileAnimation = getProfileAnimation({
    localFrame,
    fps,
    variant,
    isExiting,
    exitProgress,
  });

  // Name animation
  const nameAnimation = getNameTitleAnimation({
    localFrame,
    fps,
    isExiting,
    exitProgress,
    isTitle: false,
  });

  // Title animation
  const titleAnimation = getNameTitleAnimation({
    localFrame,
    fps,
    isExiting,
    exitProgress,
    isTitle: true,
  });

  // Quotation marks animation
  const quotationAnimation = getQuotationAnimation({
    localFrame,
    fps,
    variant,
    isExiting,
    exitProgress,
  });

  // Project code animation
  const projectAnimation = getProjectCodeAnimation({
    localFrame,
    fps,
    feedbackLength: feedbackHighlight?.length || 0,
    isExiting,
    exitProgress,
  });

  // Typing text opacity for exit transition
  const typingTextOpacity = isExiting ? getExitOpacity(exitProgress) : 1;

  const renderProfileImage = () => {
    if (!processedAvatarUrl) return null;

    return (
      <div
        className={combineClasses(
          'relative shrink-0 self-start rounded-full overflow-hidden',
          'shadow-2xl border-solid bg-slate-700',
          colors.borderPrimary,
        )}
        style={{
          width: config.profileSize,
          height: config.profileSize,
          borderWidth: config.profileBorderWidth,
          opacity: profileAnimation.opacity,
          transform: `translateX(${profileAnimation.translateX}px)`,
        }}
      >
        <FallbackRemotionImg
          src={processedAvatarUrl}
          className="size-full object-cover"
        />
      </div>
    );
  };

  const renderContent = () => (
    <div
      className="flex flex-col gap-4 flex-1 mt-24"
      style={{
        alignItems: config.contentAlign === 'right' ? 'flex-end' : 'flex-start',
        textAlign: config.contentAlign,
      }}
    >
      {/* Name */}
      <p
        className={combineClasses(typography.lg, colors.textPrimary)}
        style={{
          opacity: nameAnimation.opacity,
          transform: `scale(${nameAnimation.scale})`,
        }}
      >
        {relatedName}
      </p>

      {/* Title */}
      <p
        className={combineClasses(
          typography.md,
          colors.textSecondary,
          'tracking-widest',
        )}
        style={{
          fontFamily: lalezarFontFamily,
          opacity: titleAnimation.opacity,
          transform: `scale(${titleAnimation.scale})`,
        }}
      >
        {role}
      </p>

      {/* Quote */}
      <div
        className={combineClasses(
          'min-h-[240px] max-w-xl mt-7',
          config.contentAlign === 'right' ? 'text-right' : 'text-left',
        )}
        style={{
          opacity: typingTextOpacity,
        }}
      >
        <TypingText
          text={feedbackHighlight || ''}
          startFrame={startFrame + TIMING.QUOTE.startFrame}
          charsPerFrame={TIMING.QUOTE.charsPerFrame}
          showCursor={true}
          cursorColor={rawColors.cursor}
          className={combineClasses(
            typography.xs,
            'text-frost-100 mt-4 font-semibold leading-normal',
          )}
          style={{
            fontFamily: 'Inter, sans-serif',
            textAlign: config.contentAlign,
          }}
        />
      </div>

      {/* Project Name */}
      <p
        className={combineClasses(
          'mt-10',
          typography['2xs'],
          colors.textSecondary,
          'max-w-xl',
        )}
        style={{
          opacity: projectAnimation.opacity,
          transform: `translateX(${projectAnimation.translateX}px)`,
          fontStyle: variant === 'avatar-right' ? 'italic' : 'normal',
        }}
      >
        - {projectCode}
      </p>
    </div>
  );

  const renderQuotationMarks = () => {
    if (config.quotationPosition === 'bottom-right') {
      return (
        <QuotationMarksIcon
          className={combineClasses(
            'absolute bottom-0 right-0 mr-[74px] mb-[62px] origin-center',
            colors.primaryFillIcon,
          )}
          style={{
            transform: `scale(${quotationAnimation.scale}) rotate(${quotationAnimation.rotate}deg)`,
          }}
        />
      );
    }

    // Avatar-right: quotation marks on the left
    return (
      <QuotationMarksIcon
        className={combineClasses(
          'absolute bottom-0 left-0 ml-[74px] mb-[62px] origin-center',
          colors.primaryFillIcon,
        )}
        style={{
          transform: `scale(${quotationAnimation.scale}) rotate(${quotationAnimation.rotate}deg) scaleX(-1)`,
        }}
      />
    );
  };

  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="flex items-center gap-14 px-24">
        {/* Avatar Left Layout */}
        {variant === 'avatar-left' && (
          <>
            {renderProfileImage()}
            {renderContent()}
          </>
        )}

        {/* Avatar Right Layout */}
        {variant === 'avatar-right' && (
          <>
            {renderQuotationMarks()}
            {renderContent()}
            {renderProfileImage()}
          </>
        )}
      </div>

      {/* Quotation marks for avatar-left variant */}
      {variant === 'avatar-left' && renderQuotationMarks()}
    </div>
  );
};
